import { OverviewChartType, type OverviewChart } from "@ibada/shared-types/overview"

/** True when the chart has labels and at least one non-empty numeric series. */
export function chartHasRenderableSeriesData(chart: OverviewChart): boolean {
  if (!Array.isArray(chart.labels) || chart.labels.length === 0) return false
  return chart.series.some(
    (series) =>
      Array.isArray(series.data) &&
      series.data.length > 0 &&
      series.data.some((point) => typeof point === "number" && Number.isFinite(point) && point !== 0),
  )
}

export function chartHasRenderableSegments(chart: OverviewChart): boolean {
  return Boolean(
    chart.segments?.some(
      (segment) => typeof segment.value === "number" && Number.isFinite(segment.value) && segment.value !== 0,
    ),
  )
}

export function chartHasRenderableData(chart: OverviewChart): boolean {
  if (chart.chartType === OverviewChartType.Donut) {
    return chartHasRenderableSegments(chart)
  }
  return chartHasRenderableSeriesData(chart)
}

const COUNT_LIKE_KEYS = ["net", "count", "members", "value", "total", "balance"] as const

/** Horizontal / vertical bar rows from first series whose key matches common metric keys, else first series. */
export function buildLabeledMetricBarRows(
  chart: OverviewChart,
): Array<{ label: string; value: number }> {
  const metricSeries =
    chart.series.find((s) => COUNT_LIKE_KEYS.includes(s.key as (typeof COUNT_LIKE_KEYS)[number])) ??
    chart.series[0]
  if (!metricSeries) return []

  return chart.labels
    .map((label, index) => ({
      label,
      value: metricSeries.data[index] ?? 0,
    }))
    .filter((row) => Number.isFinite(row.value) && row.value !== 0)
}

export function zipOverviewLineChart(
  chart: OverviewChart,
  valueKey: string,
): Array<Record<string, string | number>> {
  if (!chart.labels.length) return []
  const series = chart.series.find((s) => s.key === valueKey) ?? chart.series[0]
  if (!series) return []

  return chart.labels.map((label, index) => {
    const row: Record<string, string | number> = { period: label }
    row[valueKey] = series.data[index] ?? 0
    return row
  })
}
